import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Table2 } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { PlatformLogo } from './shared/PlatformLogo';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyUnit, formatStat, formatLocalDate } from '../utils/formatters';

export function DailyPerformanceTable({
  googleAdsUnifiedData, metaAdsUnifiedData, gaDailyTotalsMap,
  isLoading,
}) {
  const rows = useMemo(() => {
    const dateSet = new Set();
    (googleAdsUnifiedData || []).forEach(d => { if (d.date) dateSet.add(d.date.split('T')[0]); });
    (metaAdsUnifiedData || []).forEach(d => { if (d.date) dateSet.add(d.date.split('T')[0]); });

    const gAdsMap = {};
    (googleAdsUnifiedData || []).forEach(d => {
      const key = (d.date || '').split('T')[0];
      if (!gAdsMap[key]) gAdsMap[key] = { cost: 0, clicks: 0, conversions: 0, revenue: 0 };
      gAdsMap[key].cost += d.cost || 0;
      gAdsMap[key].clicks += d.clicks || 0;
      gAdsMap[key].conversions += d.conversions || d.conversion_value ? 1 : 0;
      gAdsMap[key].revenue += d.ga4_total_revenue || 0;
    });

    const metaMap = {};
    (metaAdsUnifiedData || []).forEach(d => {
      const key = (d.date || '').split('T')[0];
      if (!metaMap[key]) metaMap[key] = { spend: 0, clicks: 0, conversions: 0, revenue: 0 };
      metaMap[key].spend += d.spend || 0;
      metaMap[key].clicks += d.clicks || 0;
      metaMap[key].conversions += d.conversions || d.conversion_value ? 1 : 0;
      metaMap[key].revenue += d.ga4_total_revenue || 0;
    });

    return Array.from(dateSet)
      .sort((a, b) => b.localeCompare(a))
      .slice(0, 30)
      .map(date => {
        const g = gAdsMap[date] || {};
        const m = metaMap[date] || {};
        const ga = gaDailyTotalsMap?.get(date) || {};
        return { date, g, m, ga };
      });
  }, [googleAdsUnifiedData, metaAdsUnifiedData, gaDailyTotalsMap]);

  if (isLoading) return <SkeletonLoader variant="table" rows={8} />;
  if (rows.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="Daily Performance" icon={Table2} subtitle="Combined platform metrics by day" />
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modern-table w-full">
            <thead>
              <tr>
                <th className="text-left pl-4">Date</th>
                <th className="text-right"><div className="flex items-center justify-end gap-1"><PlatformLogo platform="google-ads" size="sm" /> Cost</div></th>
                <th className="text-right">G.Clicks</th>
                <th className="text-right">G.Revenue</th>
                <th className="text-right"><div className="flex items-center justify-end gap-1"><PlatformLogo platform="meta" size="sm" /> Spend</div></th>
                <th className="text-right">M.Clicks</th>
                <th className="text-right">M.Revenue</th>
                <th className="text-right"><div className="flex items-center justify-end gap-1"><PlatformLogo platform="ga4" size="sm" /> Sessions</div></th>
                <th className="text-right pr-4">GA4 Rev</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.date}>
                  <td className="pl-4 text-xs font-medium text-gray-700 whitespace-nowrap">{formatLocalDate(row.date)}</td>
                  <td className="text-right text-xs">{formatCurrencyUnit(row.g.cost)}</td>
                  <td className="text-right text-xs">{formatStat(row.g.clicks)}</td>
                  <td className="text-right text-xs font-medium">{formatCurrencyUnit(row.g.revenue)}</td>
                  <td className="text-right text-xs">{formatCurrencyUnit(row.m.spend)}</td>
                  <td className="text-right text-xs">{formatStat(row.m.clicks)}</td>
                  <td className="text-right text-xs font-medium">{formatCurrencyUnit(row.m.revenue)}</td>
                  <td className="text-right text-xs">{formatStat(row.ga.sessions)}</td>
                  <td className="text-right text-xs font-medium pr-4">{formatCurrencyUnit(row.ga.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
